import type { IncomeEntryCreationResult, IncomeEntrySummary, IncomeEntryStatus } from "types";
import { Badge, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { MdInbox } from "react-icons/md";
import IncomeEntryFileUpload from "components/IncomeEntry/IncomeEntryFileUpload";
import IncomeEntrySummaryCard from "components/IncomeEntry/IncomeEntrySummaryCard";
import PageLayout from "components/layout/PageLayout";
import { STATUS_COLOR, TABS } from "helpers/incomeEntry";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { INCOME_ENTRY_STATUS, Roles, ROUTES, STATUS_CODES } from "constants/index";
import useCrud from "hooks/useCrud";
import { INCOME_ENTRIES_URL } from "constants/urls";
import { useSession } from "providers/SessionProvider";
import { useToast } from "hooks/useToast";

export default function IncomeEntryPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<IncomeEntryStatus>(INCOME_ENTRY_STATUS.PENDING);
  const { userData } = useSession();
  const { showSnackBar } = useToast();
  const { data: incomeEntries, loading, fetchData } = useCrud<IncomeEntrySummary>(
    INCOME_ENTRIES_URL,
    {
      disableAutoFetch: false,
      authAutoFetch: true,
    },
  );

  const incomesByStatus = useMemo(() => {
    const map: Record<IncomeEntryStatus, IncomeEntrySummary[]> = {
      [INCOME_ENTRY_STATUS.PENDING]: [],
      [INCOME_ENTRY_STATUS.IN_PROGRESS]: [],
      [INCOME_ENTRY_STATUS.VERIFIED]: [],
      [INCOME_ENTRY_STATUS.FINISHED]: [],
    };
    if (!incomeEntries) return map;
    for (const income of incomeEntries) map[income.status].push(income);
    return map;
  }, [incomeEntries]);

  const visibles = incomesByStatus[tab];

  async function handleCreated(result: IncomeEntryCreationResult) {
    try {
      const response = await fetchData(undefined, true);
      if (response.status !== STATUS_CODES.OK) throw new Error("List refresh failed");
      setTab(result.status);
    } catch {
      showSnackBar("El preingreso se creo, pero no se pudo actualizar el listado. Actualice la pagina.", "warning");
    }
  }

  const canCreate = userData?.role === Roles.SUPERADMIN ||
    userData?.role === Roles.ADMIN || userData?.role === Roles.SUPERVISOR;
  const importButton = canCreate ? <IncomeEntryFileUpload onCreated={handleCreated} /> : undefined;

  const handleClickIncome = (id: string, status: string) => {
    switch(userData?.role){
      case Roles.OPERATOR:
        if (status === INCOME_ENTRY_STATUS.VERIFIED || status === INCOME_ENTRY_STATUS.FINISHED) break;
        navigate(ROUTES.INCOME_ENTRY_DETAIL.replace(":id", id));
        break;
      case Roles.ADMIN:
      case Roles.SUPERADMIN:
      case Roles.SUPERVISOR:
        if(status !== INCOME_ENTRY_STATUS.VERIFIED && status !== INCOME_ENTRY_STATUS.FINISHED) break;
        navigate(ROUTES.INCOME_ENTRY_DETAIL_REVIEW.replace(":id", id));
        break;
      default:
        break;
    }
  };

  if (loading || !incomeEntries) {
    return (
      <PageLayout
        importButton={importButton}
        pageTitle="Ingresos"
        pageDesc="Verificá la mercadería contra el remito"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 8, textAlign: "center" }}
        >
          Cargando...
        </Typography>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      importButton={importButton}
      pageTitle="Ingresos"
      pageDesc="Verificá la mercadería contra el remito"
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as IncomeEntryStatus)}
        variant="fullWidth"
        sx={{
          position: "sticky",
          top: "4rem", // appBar height
          zIndex: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 48,
          "& .MuiTabs-indicator": { bgcolor: `${STATUS_COLOR[tab]}.main` },
        }}
      >
        {TABS.map(({ status, label, roles }) => {
          const count = incomesByStatus[status].length;
          const color = STATUS_COLOR[status];
          if (roles && !roles.includes(userData?.role ?? "")) return null;
          return (
            <Tab
              key={status}
              value={status}
              sx={{
                minHeight: 48,
                textTransform: "none",
                fontWeight: 600,
                overflow: "visible",
                "&.Mui-selected": { color: `${color}.main` },
              }}
              label={
                status === INCOME_ENTRY_STATUS.PENDING ? (
                  <Badge
                    color="error"
                    badgeContent={count}
                    sx={{ "& .MuiBadge-badge": { right: -14, top: 2 } }}
                  >
                    {label}
                  </Badge>
                ) : (
                  `${label}${count ? ` (${count})` : ""}`
                )
              }
            />
          );
        })}
      </Tabs>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          maxWidth: "100%",
          overflowY: "auto",
          px: 2,
          pt: 1.5,
          pb: 2,
          gap: 1.25,
        }}
      >
        {visibles.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ py: 8, opacity: 0.6 }}
          >
            <MdInbox size={40} />
            <Typography variant="body2" color="text.secondary">
              No hay ingresos en esta lista
            </Typography>
          </Stack>
        ) : (
          visibles.map((income) => (
            <IncomeEntrySummaryCard
              key={income.id}
              income={income}
              onClick={handleClickIncome}
            />
          ))
        )}
      </Box>
    </PageLayout>
  );
}
